import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
// services
import { MasterService } from "../../../services/master.service";
import { MessageService } from "../../../services/message.service";
import { StoreService } from "../../../services/store.service";
import { SecurityService } from "src/app/services/security.service";
import * as languageLibrary from '../../../services/language';
import { ReceivableSummary } from "src/app/models/receivable-summary.model";
import { Receivable } from "src/app/models/receivable.model";
import { Lot } from "src/app/models/lot.model";
import { clean } from "src/app/utils/numeric";
import { formatCurrency } from "src/app/utils/format";
import moment from "moment";
import { Subscription } from "rxjs";

@Component({
    selector: "borrowingbase-report",
    templateUrl: "./borrowingbase-report.component.html",
    styleUrls: ["./borrowingbase-report.component.scss"],
    standalone: false
})
export class BorrowingBaseReportComponent implements OnInit, OnDestroy {

    /** define if is loading */ 
    protected loading: boolean = false

    // save the language
    protected language: string | null = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    /** */
    protected lots: Lot[] = []

    // The search form group
    protected form: FormGroup | null = null

    /** A summary of aging data for the report. */
    protected summary: ReceivableSummary | null = null

    /** A list of receivables for the selected lot and/or corporation. */
    protected receivables: Receivable[] | null = null

    /** The list of eligible contracts based on the user selections.  */
    protected eligible = { 
        summary: new ReceivableSummary(),
        receivables: new Array<Receivable>()
    }
    
    /** The list of ineligible contracts based on the user selections. */
    protected ineligible = { 
        summary: new ReceivableSummary(),
        receivables: new Array<Receivable>()
    }

    // A debounce timer for search events. Used to wait for 
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    // Listen for changes to the filters.
    private subscriptions: Subscription[] | null = []

    /** If the report is filtered by lot this will have a positive value. */
    protected get lotId () {
        return parseInt(this.form.get('lotId').value)
    }

    /**
     * Determine a human-readable string of 
     * the current sum filter user selection.
     */
    protected get summedBy (): string {
        switch((this.form.get('sum').value || '').toLowerCase()) {
            case 'principal':
                return 'Principal'
            case 'principalwithtax':
                return 'Principal + Tax'
            case 'payment':
                return 'Payment'
        }

        return null
    }

    protected get commitment (): number {
        return clean(this.form.get('commitment').value)
    }

    protected get advances (): number {
        return clean(this.form.get('advances').value)
    }

    constructor(private master: MasterService, private ms: MessageService, protected store: StoreService, private router: Router, private fb: FormBuilder, protected security: SecurityService) {
        this.form = this.fb.group({
            lotId: new FormControl<number>(0, []),
            sum: new FormControl<string>('principal', []),
            days: new FormControl<number>(60, []),
            term: new FormControl<number>(48, []),
            percent: new FormControl<number>(50, []),
            commitment: new FormControl<string>(formatCurrency(0), []),
            advances: new FormControl<string>(formatCurrency(0), []),
            dated: new FormControl<string>(moment().format('YYYY-MM-DD'), [])
        })
    }

    ngOnInit() {
        this.subscriptions.push(
            this.form.get('lotId').valueChanges.subscribe((id) => {
                this.reset()
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.form.get('days').valueChanges.subscribe((_) => {
                this.determineEligibility()
            })
        )

        this.subscriptions.push(
            this.form.get('term').valueChanges.subscribe((_) => {
                this.determineEligibility()
            })
        )

        this.reset()
        this.fetch()

        if (this.store && this.store.userAccount && this.store.userAccount['user'] && 
            this.store.userAccount['user'].lots) {
            this.lots = this.store.userAccount['user'].lots
        }
    }

    ngOnDestroy() {
        this.reset()

        if (this.watcher) {
            clearTimeout(this.watcher)
            this.watcher = null
        }

        for (const subscription of this.subscriptions) {
            subscription.unsubscribe()
        }

        this.subscriptions = null
    }

    /**
     * Reset the pagination parameters and scroll to the top
    */
    private reset() {
        this.receivables = []
        this.resetEligibility()

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    /**
     * An input has changed so lets reset so we can rebuild the groupings.
     */
    private resetEligibility() {
        this.eligible.summary = new ReceivableSummary()
        this.eligible.receivables.length = 0
        this.eligible.receivables = new Array<Receivable>()

        this.ineligible.summary = new ReceivableSummary()
        this.ineligible.receivables.length = 0
        this.ineligible.receivables = new Array<Receivable>()
    }

    /**
     * Get the summary and list of receivables needed for the report.
     */
    protected fetch = async () => {
        if (this.security.notPermitted('reports', 'portfolio aging', 'view')) {
            return
        }

        if (this.loading) { // already getting the data
            return
        }

        let lotParam = ''
        let id = parseInt(this.form.get('lotId').value)
        if (id && (!isNaN(id) && (id > 0))) {
            lotParam = `?lot=${id}`
        }

        this.loading = true
        await this.master.getAsync(`reports/borrowingbase${lotParam}`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }
            
            this.summary = res.data.summary
            this.receivables = res.data.receivables
            this.determineEligibility()
        })
    }

    /**
     * Group the receivables into eligibility 
     * buckets based on the current user selections.
     */
    private determineEligibility = () => {
        this.resetEligibility()

        if (!this.receivables || (this.receivables.length <= 0)) {
            return
        }

        const maxMonths = parseInt(this.form.get('term').value)
        const maxDays = parseInt(this.form.get('days').value)

        for (const receivable of this.receivables) {
            const months = this.toMonths(receivable.periods, receivable.frequency)
            const daysPastDue = moment().diff(receivable.pastDueDate || receivable.due, 'days')            
            receivable.daysPastDue = daysPastDue

            if ((daysPastDue > maxDays) || (months > maxMonths)) {
                this.appendIneligible(receivable)
                continue
            }

            this.appendEligible(receivable)
        }
    }

    /**
     * Add the given receivable to the eligible bucket
     * @param receivable The receivable to add to the eligible bucket
     */
    private appendEligible = (receivable: Receivable) => {
        if (!receivable || !receivable.id) {
            return
        }

        this.eligible.summary.count++
        this.eligible.summary.paymentBalance += receivable.paymentBalance
        this.eligible.summary.balance += receivable.balance
        this.eligible.summary.taxBalance += receivable.taxBalance

        switch (true) {
            case (receivable.daysPastDue < 30):
                this.eligible.summary.current++
                this.eligible.summary.currentBalance += receivable.balance
                this.eligible.summary.currentWithTaxBalance += receivable.balance + receivable.taxBalance
                this.eligible.summary.currentPaymentBalance += receivable.paymentBalance
                break
            case ((receivable.daysPastDue >= 30) && (receivable.daysPastDue <= 59)):
                this.eligible.summary.thirtyDay++
                this.eligible.summary.thirtyDayBalance += receivable.balance
                this.eligible.summary.thirtyDayWithTaxBalance += receivable.balance + receivable.taxBalance
                this.eligible.summary.thirtyDayPaymentBalance += receivable.paymentBalance
                break
            case ((receivable.daysPastDue >= 60) && (receivable.daysPastDue <= 89)):
                this.eligible.summary.sixtyDay++
                this.eligible.summary.sixtyDayBalance += receivable.balance
                this.eligible.summary.sixtyDayWithTaxBalance += receivable.balance + receivable.taxBalance
                this.eligible.summary.sixtyDayPaymentBalance += receivable.paymentBalance
                break
            case (receivable.daysPastDue >= 90):
                this.eligible.summary.ninetyDay++
                this.eligible.summary.ninetyDayBalance += receivable.balance
                this.eligible.summary.ninetyDayWithTaxBalance += receivable.balance + receivable.taxBalance
                this.eligible.summary.ninetyDayPaymentBalance += receivable.paymentBalance
                break
        }

        this.eligible.receivables.push(receivable)
    }

    /**
     * Add the given receivable to the ineligible bucket
     * @param receivable The receivable to add to the ineligible bucket
     */
    private appendIneligible = (receivable: Receivable) => {
        if (!receivable || !receivable.id) {
            return
        }

        this.ineligible.summary.count++
        this.ineligible.summary.paymentBalance += receivable.paymentBalance
        this.ineligible.summary.balance += receivable.balance
        this.ineligible.summary.taxBalance += receivable.taxBalance

        switch (true) {
            case (receivable.daysPastDue < 30):
                this.ineligible.summary.current++
                this.ineligible.summary.currentBalance += receivable.balance
                this.ineligible.summary.currentWithTaxBalance += receivable.balance + receivable.taxBalance
                this.ineligible.summary.currentPaymentBalance += receivable.paymentBalance
                break
            case ((receivable.daysPastDue >= 30) && (receivable.daysPastDue <= 59)):
                this.ineligible.summary.thirtyDay++
                this.ineligible.summary.thirtyDayBalance += receivable.balance
                this.ineligible.summary.thirtyDayWithTaxBalance += receivable.balance + receivable.taxBalance
                this.ineligible.summary.thirtyDayPaymentBalance += receivable.paymentBalance
                break
            case ((receivable.daysPastDue >= 60) && (receivable.daysPastDue <= 89)):
                this.ineligible.summary.sixtyDay++
                this.ineligible.summary.sixtyDayBalance += receivable.balance
                this.ineligible.summary.sixtyDayWithTaxBalance += receivable.balance + receivable.taxBalance
                this.ineligible.summary.sixtyDayPaymentBalance += receivable.paymentBalance
                break
            case (receivable.daysPastDue >= 90):
                this.ineligible.summary.ninetyDay++
                this.ineligible.summary.ninetyDayBalance += receivable.balance
                this.ineligible.summary.ninetyDayWithTaxBalance += receivable.balance + receivable.taxBalance
                this.ineligible.summary.ninetyDayPaymentBalance += receivable.paymentBalance
                break
        }

        this.ineligible.receivables.push(receivable)
    }

    /**
    * Calculate the given term in months based on 
    * the current payment frequency.
    * @param term The current number of payments to determine months with.
    * @param frequency The payment cycle for the loan.
    * @returns The number of months in the given term and known frequency (cycle).
    */
    protected toMonths(term: number, frequency: string): number {
        if (!frequency || (frequency.length <= 0)) {
            return term
        }

        switch (frequency.toLowerCase()) {
            case 'weekly': // once a week
                return Math.round((term / 52) * 12)
            case 'bi-weekly': // every two weeks
            case 'biweekly':
                return Math.round((term / 26) * 12)
            case 'semi-monthly': // twice a month
            case 'semimonthly':
                return Math.round(term / 2)
            case 'bi-monthly': // every two months
                return Math.round(term * 2)
            case 'quaterly': // every three months
                return Math.round(term * 3)
            case 'annually':
                return Math.round(term * 12)
            default: // once a month (i.e. monthly)
                return term
        }
    }

    /**
     * Get the needed balance based on the sum user filter.
     * @param summary A receivable or receivable summary object that will contain the balance to retrieve.
     * @param bucket The aging bucket whose balance we need (current, thirtyDay, sixtyDay, ninetyDay)
     * @returns The balance based on the sum filter.
     */
    protected balance = (summary: Object, bucket: string): number => {
        switch((this.form.get('sum').value || '').toLowerCase()) {
            case 'principal':
                if (!bucket || (bucket.length <= 0)) {
                    return summary['balance']
                }

                return summary[`${bucket}Balance`]
            case 'principalwithtax':
                if (!bucket || (bucket.length <= 0)) {
                    return (summary['balance'] + summary['taxBalance'])
                }

                return summary[`${bucket}WithTaxBalance`]
            case 'payment':
                if (!bucket || (bucket.length <= 0)) {
                    return summary['paymentBalance']
                }

                return summary[`${bucket}PaymentBalance`]
        }

        return 0
    }

    /**
     * Get the lesser of the borrowing base and commitment values
     */
    protected lesser = (): number => {
        if (this.commitment <= 0) {
            return 0
        }

        const base = clean(this.form.get('percent').value)
        if (base <= 0) {
            return 0
        }

        const amount = this.balance(this.eligible.summary, null)
        if (amount <= 0) {
            return 0
        }

        return Math.min(this.commitment, (amount * (base / 100)))
    }

    /**
     * Get the lesser of borrow base and commitment less any advances already made.
     */
    protected available = (): number => {
        const result = this.lesser() - this.advances
        if (result <= 0) {
            return 0
        }

        return result
    }

    /**
     * Computed how many says past due the account is based on the given due date.
     * @param {Date} ago The current (or past) due date of the account.
     */
    protected daysAgo = (ago: Date): number => {
        return moment().diff(ago, 'days')
    }
    
    /**
     * listen to output of navbar
    */ 
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'setPermissions':
                this.lots = this.store.userAccount['user'].lots
                this.reset()
                this.fetch()
                break
            case 'changeLot':
                break
        }
    }
}
